import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  LinearProgress,
  Tooltip,
  Select,
  MenuItem,
  InputAdornment,
  Tabs,
  Tab,
  Skeleton,
} from '@mui/material';
import {
  PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import SearchIcon from '@mui/icons-material/Search';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import YouTubeIcon from '@mui/icons-material/YouTube';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import ArticleIcon from '@mui/icons-material/Article';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopIcon from '@mui/icons-material/Stop';
import api from '../services/api';
import {
  submitPodcastTranscription,
  listPodcasts,
  getPodcastDetail,
  deletePodcast,
  retryPodcast,
  downloadPodcastPdf,
  downloadWeeklyPodcastDigestPdf,
  getWhisperStatus,
  stopWhisperServer,
  PodcastListItem,
  PodcastDetail,
  PodcastStatus,
} from '../services/api';
import { TickerChip } from '../components/TickerActionMenu';
import TranscriptsTab from '../components/TranscriptsTab';
import { bloomberg } from '../theme/bloombergTheme';

const POLL_INTERVAL_MS = 3000;

const statusProgress: Record<PodcastStatus, number> = {
  pending: 5,
  downloading: 20,
  transcribing: 50,
  summarizing: 80,
  completed: 100,
  failed: 0,
};

const statusLabel: Record<PodcastStatus, string> = {
  pending: 'Queued...',
  downloading: 'Downloading audio...',
  transcribing: 'Transcribing (this may take a few minutes)...',
  summarizing: 'Generating investment summary...',
  completed: 'Completed',
  failed: 'Failed',
};

const sentimentColors: Record<string, { bg: string; text: string }> = {
  overall_bullish: { bg: bloomberg.status.positiveBg, text: bloomberg.status.positive },
  overall_bearish: { bg: bloomberg.status.negativeBg, text: bloomberg.status.negative },
  mixed: { bg: bloomberg.status.warningBg, text: bloomberg.status.warning },
  neutral: { bg: bloomberg.bg.tertiary, text: bloomberg.text.muted },
};

const getPlatformIcon = (platform: string | null) => {
  switch (platform) {
    case 'youtube':
      return <YouTubeIcon sx={{ color: '#FF0000', fontSize: 20 }} />;
    case 'spotify':
      return <MusicNoteIcon sx={{ color: '#1DB954', fontSize: 20 }} />;
    case 'apple':
      return <PodcastsIcon sx={{ color: '#9933CC', fontSize: 20 }} />;
    case 'rss_auto':
    case 'direct':
      return <PodcastsIcon sx={{ color: '#FF9500', fontSize: 20 }} />;
    default:
      return <PodcastsIcon sx={{ color: bloomberg.text.muted, fontSize: 20 }} />;
  }
};

const formatDuration = (seconds: number | null): string => {
  if (!seconds) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

interface BriefEpisode {
  id: number; title: string; feed_name: string; completed_at: string | null;
  sentiment: string; bullish_signals: string[]; bearish_signals: string[];
  macro_themes: string[]; sectors_mentioned: string[];
}
interface PieEntry { name: string; value: number; }

const PIE_COLORS = ['#ff9800','#2196f3','#4caf50','#e91e63','#9c27b0','#00bcd4','#ff5722','#8bc34a','#ffc107','#3f51b5','#009688','#f44336'];

const Podcasts: React.FC = () => {
  const [url, setUrl] = useState('');
  const [mainTab, setMainTab] = useState(0);
  const [briefEpisodes, setBriefEpisodes] = useState<BriefEpisode[]>([]);
  const [briefThemePie, setBriefThemePie] = useState<PieEntry[]>([]);
  const [briefSectorPie, setBriefSectorPie] = useState<PieEntry[]>([]);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefDays, setBriefDays] = useState(7);
  const [expandedSignals, setExpandedSignals] = useState<Record<number, boolean>>({});
  const [podcasts, setPodcasts] = useState<PodcastListItem[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<PodcastDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [weeklyPdfLoading, setWeeklyPdfLoading] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterShow, setFilterShow] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterSentiment, setFilterSentiment] = useState('');
  const [whisperRunning, setWhisperRunning] = useState<boolean | null>(null);
  const [whisperBusy, setWhisperBusy] = useState(false);
  const [whisperModel, setWhisperModel] = useState<string | null>(null);
  const [whisperChecking, setWhisperChecking] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkWhisperStatus = useCallback(async () => {
    setWhisperChecking(true);
    try {
      const data = await getWhisperStatus();
      setWhisperRunning(data.running);
      setWhisperBusy(data.busy || false);
      setWhisperModel(data.model);
    } catch {
      setWhisperRunning(false);
    } finally {
      setWhisperChecking(false);
    }
  }, []);

  const handleStopWhisper = async () => {
    try {
      await stopWhisperServer();
      setTimeout(checkWhisperStatus, 1500);
    } catch {
      checkWhisperStatus();
    }
  };

  // Check whisper status on mount
  useEffect(() => {
    checkWhisperStatus();
  }, [checkWhisperStatus]);

  const loadPodcasts = useCallback(async () => {
    try {
      const data = await listPodcasts();
      setPodcasts(data);
    } catch (err) {
      console.error('Error loading podcasts:', err);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadPodcasts();
  }, [loadPodcasts]);

  // Poll when there are in-progress podcasts
  useEffect(() => {
    const hasProcessing = podcasts.some(
      (p) => !['completed', 'failed'].includes(p.status)
    );

    if (hasProcessing && !pollRef.current) {
      pollRef.current = setInterval(loadPodcasts, POLL_INTERVAL_MS);
    } else if (!hasProcessing && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [podcasts, loadPodcasts]);

  const fetchBrief = useCallback(async (days: number) => {
    setBriefLoading(true);
    try {
      const { data } = await api.get(`/api/podcasts/weekly-brief-dashboard?days=${days}`);
      setBriefEpisodes(data.episodes || []);
      setBriefThemePie(data.theme_pie || []);
      setBriefSectorPie(data.sector_pie || []);
    } catch { /* noop */ } finally {
      setBriefLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mainTab === 1) fetchBrief(briefDays);
  }, [mainTab, briefDays, fetchBrief]);

  const handleSubmit = async () => {
    const trimmed = url.trim();
    if (!trimmed) {
      setError('Please enter a podcast URL');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await submitPodcastTranscription(trimmed);
      setUrl('');
      await loadPodcasts();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit podcast');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !submitting) {
      handleSubmit();
    }
  };

  const handleExpand = async (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
      setDetail(null);
      setShowTranscript(false);
      return;
    }
    setExpandedId(id);
    setDetail(null);
    setShowTranscript(false);
    setDetailLoading(true);
    try {
      const data = await getPodcastDetail(id);
      setDetail(data);
    } catch (err) {
      console.error('Error loading podcast detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try {
      await deletePodcast(id);
      if (expandedId === id) {
        setExpandedId(null);
        setDetail(null);
      }
      await loadPodcasts();
    } catch (err) {
      console.error('Error deleting podcast:', err);
    }
  };

  const handleRetry = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try {
      await retryPodcast(id);
      await loadPodcasts();
    } catch (err) {
      console.error('Error retrying podcast:', err);
    }
  };

  const processingPodcasts = podcasts.filter(
    (p) => !['completed', 'failed'].includes(p.status)
  );
  const allCompleted = podcasts.filter((p) => p.status === 'completed');
  const failedPodcasts = podcasts.filter((p) => p.status === 'failed');

  // Filter options derived from completed podcasts
  const uniqueShows = Array.from(new Set(allCompleted.map(p => p.feed_name).filter(Boolean))) as string[];
  const uniquePlatforms = Array.from(new Set(allCompleted.map(p => p.platform).filter(Boolean))) as string[];
  const uniqueSentiments = Array.from(new Set(allCompleted.map(p => p.sentiment).filter(Boolean))) as string[];

  const completedPodcasts = allCompleted.filter(p => {
    const q = filterSearch.toLowerCase();
    const matchSearch = !q || (p.title || '').toLowerCase().includes(q) || (p.feed_name || '').toLowerCase().includes(q);
    const matchShow = !filterShow || p.feed_name === filterShow;
    const matchPlatform = !filterPlatform || p.platform === filterPlatform;
    const matchSentiment = !filterSentiment || p.sentiment === filterSentiment;
    return matchSearch && matchShow && matchPlatform && matchSentiment;
  });

  const hasFilters = filterSearch || filterShow || filterPlatform || filterSentiment;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <PodcastsIcon sx={{ fontSize: 32, color: '#1976d2' }} />
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Podcast Intelligence
        </Typography>
        <Box sx={{ flex: 1 }} />
        {/* Whisper Server Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={
            whisperRunning === null ? 'Checking...' :
            whisperBusy ? 'Whisper server busy transcribing' :
            whisperRunning ? `Whisper server running (${whisperModel || 'unknown model'})` :
            'Whisper server offline'
          }>
            <Chip
              icon={
                whisperChecking ? <CircularProgress size={12} /> :
                <FiberManualRecordIcon sx={{ fontSize: '12px !important', color: whisperBusy ? '#f59e0b' : whisperRunning ? '#22c55e' : '#ef4444' }} />
              }
              label={whisperBusy ? 'Whisper Busy' : whisperRunning ? 'Whisper Online' : 'Whisper Offline'}
              size="small"
              variant="outlined"
              onClick={checkWhisperStatus}
              sx={{
                borderColor: whisperBusy ? '#f59e0b' : whisperRunning ? '#22c55e' : '#ef4444',
                color: whisperBusy ? '#f59e0b' : whisperRunning ? '#22c55e' : '#ef4444',
                fontWeight: 600,
                fontSize: '0.75rem',
                cursor: 'pointer',
                '&:hover': { bgcolor: whisperBusy ? 'rgba(245,158,11,0.08)' : whisperRunning ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)' },
              }}
            />
          </Tooltip>
          {whisperRunning && (
            <Tooltip title="Stop whisper server">
              <IconButton size="small" onClick={handleStopWhisper} sx={{ color: '#ef4444', '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' } }}>
                <StopIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {whisperRunning === false && (
            <Tooltip title="Recheck whisper server status">
              <Button
                size="small"
                variant="outlined"
                onClick={checkWhisperStatus}
                disabled={whisperChecking}
                sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 'auto', borderColor: '#555', color: '#aaa' }}
              >
                {whisperChecking ? 'Checking...' : 'Retry'}
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Tab navigation */}
      <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Episodes" />
        <Tab label="Weekly Brief" />
        <Tab label="Transcripts" />
      </Tabs>

      {mainTab === 2 && <TranscriptsTab />}

      {/* ── Weekly Brief Dashboard ── */}
      {mainTab === 1 && (
        <Box>
          {/* Period selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Typography variant="caption" sx={{ color: bloomberg.text.secondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mr: 0.5 }}>Period:</Typography>
            {[7, 14, 30].map(d => (
              <Chip key={d} label={`${d}D`} size="small" onClick={() => setBriefDays(d)}
                sx={{ height: 24, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  bgcolor: briefDays === d ? bloomberg.accent.orange : 'transparent',
                  color: briefDays === d ? '#000' : bloomberg.text.secondary,
                  border: `1px solid ${briefDays === d ? bloomberg.accent.orange : bloomberg.border}`,
                  '&:hover': { bgcolor: briefDays === d ? bloomberg.accent.orange : `${bloomberg.accent.orange}22` },
                }} />
            ))}
            <Box sx={{ flex: 1 }} />
            <Tooltip title={`Download Weekly Brief PDF (last ${briefDays} days)`}>
              <span>
                <Button size="small" variant="outlined" startIcon={weeklyPdfLoading ? <CircularProgress size={14} /> : null}
                  disabled={weeklyPdfLoading}
                  onClick={async () => { setWeeklyPdfLoading(true); try { await downloadWeeklyPodcastDigestPdf(briefDays); } finally { setWeeklyPdfLoading(false); } }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', borderColor: 'divider', color: 'text.secondary' }}>
                  Download PDF
                </Button>
              </span>
            </Tooltip>
          </Box>

          {briefLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" height={60} />)}
            </Box>
          ) : briefEpisodes.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderColor: 'divider', border: '1px dashed' }}>
              <Typography color="text.secondary">No completed podcasts in the last {briefDays} days.</Typography>
            </Paper>
          ) : (
            <>
              {/* Signals table */}
              <Paper elevation={2} sx={{ mb: 3, overflow: 'hidden' }}>
                <Box sx={{ p: 2, borderBottom: `1px solid ${bloomberg.border}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Investment Signals by Episode</Typography>
                  <Chip label={`${briefEpisodes.length} episodes`} size="small" sx={{ fontSize: 11, height: 20, bgcolor: bloomberg.accent.orange + '22', color: bloomberg.accent.orange }} />
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <Box component="thead">
                      <Box component="tr" sx={{ borderBottom: `2px solid ${bloomberg.accent.orange}`, bgcolor: bloomberg.bg?.secondary }}>
                        {['Podcast', 'Bullish Signals', 'Bearish Signals', 'Thematic', 'Sectors'].map((h, i) => (
                          <Box key={h} component="th" sx={{ py: 1, px: 1.5, textAlign: 'left', fontSize: 11, fontWeight: 700, color: bloomberg.accent.orange, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', minWidth: i === 0 ? 200 : 180 }}>{h}</Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {briefEpisodes.map(ep => {
                        const sc = sentimentColors[ep.sentiment] || sentimentColors.neutral;
                        const expanded = expandedSignals[ep.id];
                        const bullish = expanded ? ep.bullish_signals : ep.bullish_signals.slice(0, 3);
                        const bearish = expanded ? ep.bearish_signals : ep.bearish_signals.slice(0, 3);
                        const themes = expanded ? ep.macro_themes : ep.macro_themes.slice(0, 3);
                        const sectors = expanded ? ep.sectors_mentioned : ep.sectors_mentioned.slice(0, 3);
                        const hasMore = ep.bullish_signals.length > 3 || ep.bearish_signals.length > 3 || ep.macro_themes.length > 3 || ep.sectors_mentioned.length > 3;
                        return (
                          <Box component="tr" key={ep.id} sx={{ borderBottom: `1px solid ${bloomberg.border}`, verticalAlign: 'top', '&:hover': { bgcolor: `${bloomberg.accent.orange}06` } }}>
                            {/* Podcast Name */}
                            <Box component="td" sx={{ py: 1.2, px: 1.5 }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, mb: 0.3 }}>{ep.title}</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                {ep.feed_name && <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>{ep.feed_name}</Typography>}
                                <Chip label={ep.sentiment?.replace('overall_', '')} size="small" sx={{ height: 16, fontSize: 10, fontWeight: 700, bgcolor: sc.bg, color: sc.text }} />
                              </Box>
                              {ep.completed_at && <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, mt: 0.3 }}>{formatDate(ep.completed_at)}</Typography>}
                            </Box>
                            {/* Bullish */}
                            <Box component="td" sx={{ py: 1.2, px: 1.5 }}>
                              {bullish.length === 0 ? <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>—</Typography> : bullish.map((s, i) => (
                                <Box key={i} sx={{ display: 'flex', gap: 0.5, mb: 0.3, alignItems: 'flex-start' }}>
                                  <Typography sx={{ fontSize: 11, color: '#4caf50', mt: '1px', flexShrink: 0 }}>▲</Typography>
                                  <Typography sx={{ fontSize: 11, color: bloomberg.text.primary, lineHeight: 1.4 }}>{s}</Typography>
                                </Box>
                              ))}
                              {hasMore && !expanded && ep.bullish_signals.length > 3 && <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>+{ep.bullish_signals.length - 3} more</Typography>}
                            </Box>
                            {/* Bearish */}
                            <Box component="td" sx={{ py: 1.2, px: 1.5 }}>
                              {bearish.length === 0 ? <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>—</Typography> : bearish.map((s, i) => (
                                <Box key={i} sx={{ display: 'flex', gap: 0.5, mb: 0.3, alignItems: 'flex-start' }}>
                                  <Typography sx={{ fontSize: 11, color: '#f44336', mt: '1px', flexShrink: 0 }}>▼</Typography>
                                  <Typography sx={{ fontSize: 11, color: bloomberg.text.primary, lineHeight: 1.4 }}>{s}</Typography>
                                </Box>
                              ))}
                              {hasMore && !expanded && ep.bearish_signals.length > 3 && <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>+{ep.bearish_signals.length - 3} more</Typography>}
                            </Box>
                            {/* Thematic */}
                            <Box component="td" sx={{ py: 1.2, px: 1.5 }}>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                                {themes.length === 0 ? <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>—</Typography> : themes.map((t, i) => (
                                  <Chip key={i} label={t} size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#9c27b022', color: '#ce93d8' }} />
                                ))}
                                {!expanded && ep.macro_themes.length > 3 && <Chip label={`+${ep.macro_themes.length - 3}`} size="small" sx={{ height: 18, fontSize: 10, bgcolor: bloomberg.border, color: bloomberg.text.muted }} />}
                              </Box>
                            </Box>
                            {/* Sectors */}
                            <Box component="td" sx={{ py: 1.2, px: 1.5 }}>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, alignItems: 'flex-start' }}>
                                {sectors.length === 0 ? <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>—</Typography> : sectors.map((s, i) => (
                                  <Chip key={i} label={s} size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#2196f322', color: '#90caf9' }} />
                                ))}
                                {!expanded && ep.sectors_mentioned.length > 3 && <Chip label={`+${ep.sectors_mentioned.length - 3}`} size="small" sx={{ height: 18, fontSize: 10, bgcolor: bloomberg.border, color: bloomberg.text.muted }} />}
                              </Box>
                              {hasMore && (
                                <Typography onClick={() => setExpandedSignals(prev => ({ ...prev, [ep.id]: !expanded }))}
                                  sx={{ fontSize: 10, color: bloomberg.accent.orange, cursor: 'pointer', mt: 0.5, '&:hover': { textDecoration: 'underline' } }}>
                                  {expanded ? 'Show less' : 'Show all'}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </Paper>

              {/* Pie charts */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                {/* Thematic distribution */}
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#ce93d8' }}>Thematic Distribution</Typography>
                  {briefThemePie.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No thematic data this period.</Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={briefThemePie} cx="50%" cy="45%" outerRadius={90} dataKey="value" label={({ name, percent }) => percent > 0.07 ? `${(percent * 100).toFixed(0)}%` : ''} labelLine={false}>
                          {briefThemePie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <RechartsTooltip formatter={(value: number, name: string) => [`${value} mention${value !== 1 ? 's' : ''}`, name]} contentStyle={{ backgroundColor: '#1a1a2e', border: `1px solid ${bloomberg.border}`, fontSize: 12 }} />
                        <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Paper>

                {/* Sector distribution */}
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#90caf9' }}>Sector Distribution</Typography>
                  {briefSectorPie.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No sector data this period.</Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={briefSectorPie} cx="50%" cy="45%" outerRadius={90} dataKey="value" label={({ name, percent }) => percent > 0.07 ? `${(percent * 100).toFixed(0)}%` : ''} labelLine={false}>
                          {briefSectorPie.map((_, i) => <Cell key={i} fill={PIE_COLORS[(i + 4) % PIE_COLORS.length]} />)}
                        </Pie>
                        <RechartsTooltip formatter={(value: number, name: string) => [`${value} mention${value !== 1 ? 's' : ''}`, name]} contentStyle={{ backgroundColor: '#1a1a2e', border: `1px solid ${bloomberg.border}`, fontSize: 12 }} />
                        <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Paper>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* ── Episodes tab ── */}
      {mainTab === 0 && <>

      {/* URL Input */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
          Paste a podcast URL (YouTube, Spotify, Apple Podcasts, or direct MP3 link)
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="https://www.youtube.com/watch?v=... or https://open.spotify.com/episode/..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={submitting}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !url.trim()}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            sx={{ minWidth: 140, borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
          >
            {submitting ? 'Submitting...' : 'Transcribe'}
          </Button>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Processing Podcasts */}
      {processingPodcasts.map((p) => (
        <Card
          key={p.id}
          sx={{ mb: 2, border: '1px solid', borderColor: 'info.light', borderRadius: 2 }}
        >
          <CardContent sx={{ pb: '12px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {getPlatformIcon(p.platform)}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                {p.title || 'Processing...'}
              </Typography>
              <Chip
                label={statusLabel[p.status]}
                size="small"
                color="info"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={statusProgress[p.status]}
              sx={{ borderRadius: 1, height: 6 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {statusLabel[p.status]}
            </Typography>
          </CardContent>
        </Card>
      ))}

      {/* Failed Podcasts */}
      {failedPodcasts.map((p) => (
        <Card
          key={p.id}
          sx={{ mb: 2, border: '1px solid', borderColor: 'error.light', borderRadius: 2 }}
        >
          <CardContent sx={{ pb: '12px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ flex: 1, color: 'error.main' }}>
                Failed: {p.title || p.url}
              </Typography>
              <Tooltip title="Retry">
                <IconButton size="small" onClick={(e) => handleRetry(e, p.id)} sx={{ '&:hover': { color: 'primary.main' } }}>
                  <ReplayIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={(e) => handleDelete(e, p.id)} sx={{ '&:hover': { color: 'error.main' } }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {p.error_message && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {p.error_message}
              </Typography>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Completed Podcasts — header + filter bar */}
      {allCompleted.length > 0 && (
        <Box sx={{ mt: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Saved Podcasts
            </Typography>
            <Typography variant="body2" sx={{ color: bloomberg.text.secondary }}>
              {hasFilters ? `${completedPodcasts.length} of ${allCompleted.length}` : allCompleted.length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search title or show…"
              value={filterSearch}
              onChange={e => setFilterSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: 220, flex: 1 }}
            />
            {uniqueShows.length > 0 && (
              <Select
                size="small"
                displayEmpty
                value={filterShow}
                onChange={e => setFilterShow(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value=""><em>All Shows</em></MenuItem>
                {uniqueShows.sort().map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            )}
            {uniquePlatforms.length > 1 && (
              <Select
                size="small"
                displayEmpty
                value={filterPlatform}
                onChange={e => setFilterPlatform(e.target.value)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value=""><em>All Platforms</em></MenuItem>
                {uniquePlatforms.sort().map(p => (
                  <MenuItem key={p} value={p} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {getPlatformIcon(p)}&nbsp;{p.charAt(0).toUpperCase() + p.slice(1).replace('_', ' ')}
                  </MenuItem>
                ))}
              </Select>
            )}
            {uniqueSentiments.length > 0 && (
              <Select
                size="small"
                displayEmpty
                value={filterSentiment}
                onChange={e => setFilterSentiment(e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value=""><em>All Sentiments</em></MenuItem>
                {uniqueSentiments.map(s => (
                  <MenuItem key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</MenuItem>
                ))}
              </Select>
            )}
            {hasFilters && (
              <Button size="small" onClick={() => { setFilterSearch(''); setFilterShow(''); setFilterPlatform(''); setFilterSentiment(''); }}>
                Clear
              </Button>
            )}
          </Box>
        </Box>
      )}

      {completedPodcasts.map((p) => (
        <Card
          key={p.id}
          sx={{
            mb: 2,
            border: '1px solid',
            borderColor: expandedId === p.id ? 'primary.main' : 'divider',
            borderRadius: 2,
            transition: 'border-color 0.2s',
          }}
        >
          <CardActionArea onClick={() => handleExpand(p.id)} sx={{ px: 2, py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {getPlatformIcon(p.platform)}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                  {p.title || 'Untitled Podcast'}
                </Typography>
                {p.feed_name && (
                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500, lineHeight: 1.2 }}>
                    {p.feed_name}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.25 }}>
                  {p.duration_seconds && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                      <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {formatDuration(p.duration_seconds)}
                      </Typography>
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(p.completed_at)}
                  </Typography>
                </Box>
              </Box>
              <Tooltip title="Download PDF Report">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); downloadPodcastPdf(p.id); }}
                  sx={{ '&:hover': { color: bloomberg.accent.orange } }}
                >
                  <PictureAsPdfIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={(e) => handleDelete(e, p.id)}
                  sx={{ '&:hover': { color: 'error.main' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {expandedId === p.id ? (
                <ExpandLessIcon sx={{ color: 'text.secondary' }} />
              ) : (
                <ExpandMoreIcon sx={{ color: 'text.secondary' }} />
              )}
            </Box>
          </CardActionArea>

          {/* Expanded Detail */}
          <Collapse in={expandedId === p.id}>
            <Divider />
            <Box sx={{ p: 2.5 }}>
              {detailLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              )}

              {detail && detail.id === p.id && detail.summary && (
                <Box>
                  {/* Podcast Title */}
                  {detail.summary.podcast_title && (
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, lineHeight: 1.3 }}>
                      {detail.summary.podcast_title}
                    </Typography>
                  )}

                  {/* Guests */}
                  {(detail.summary.guests?.length ?? 0) > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                      {(detail.summary.guests ?? []).map((guest: string, i: number) => (
                        <Chip
                          key={i}
                          label={guest}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            borderColor: bloomberg.accent.orange,
                            color: bloomberg.text.primary,
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {/* Sentiment Badge */}
                  {detail.summary.sentiment_assessment && (
                    <Chip
                      label={detail.summary.sentiment_assessment.replace('_', ' ').toUpperCase()}
                      size="small"
                      sx={{
                        mb: 2,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        bgcolor: (sentimentColors[detail.summary.sentiment_assessment] || sentimentColors.neutral).bg,
                        color: (sentimentColors[detail.summary.sentiment_assessment] || sentimentColors.neutral).text,
                      }}
                    />
                  )}

                  {/* Executive Summary */}
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Executive Summary
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 3, lineHeight: 1.7, whiteSpace: 'pre-line' }}
                  >
                    {detail.summary.executive_summary}
                  </Typography>

                  {/* Key Points */}
                  {(() => {
                    const points = detail.summary.key_points?.length > 0 ? detail.summary.key_points : detail.summary.key_takeaways;
                    if (!points?.length) return null;
                    const splitPoint = (text: string) => {
                      const m = text.match(/^(.+?)\s*[-–—:,]\s+(.+)$/);
                      return m ? { lead: m[1], rest: m[2] } : { lead: null, rest: text };
                    };
                    return (
                    <>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Key Points
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mb: 3 }}>
                        {points.map((t: string, i: number) => {
                          const { lead, rest } = splitPoint(t);
                          return (
                            <Paper
                              key={i}
                              elevation={0}
                              sx={{
                                p: 1.5,
                                bgcolor: bloomberg.bg.secondary,
                                borderLeft: `3px solid ${bloomberg.accent.orange}`,
                                borderRadius: 1,
                              }}
                            >
                              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                {lead ? (
                                  <><span style={{ fontWeight: 700, color: 'var(--bb-text-primary)' }}>{lead}</span> — {rest}</>
                                ) : rest}
                              </Typography>
                            </Paper>
                          );
                        })}
                      </Box>
                    </>
                    );
                  })()}

                  {/* Topics Discussed */}
                  {detail.summary.topics_discussed?.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        Topics Discussed
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {detail.summary.topics_discussed.map((topic, i) => (
                          <Chip key={i} label={topic} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Suggested Trades */}
                  {detail.summary.suggested_trades && (
                    (detail.summary.suggested_trades.buy?.length > 0 || detail.summary.suggested_trades.avoid?.length > 0)
                  ) && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Suggested Trades
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        {/* BUY column */}
                        <Paper elevation={0} sx={{ p: 2, bgcolor: bloomberg.bg.secondary, borderRadius: 1.5, borderTop: '2px solid #16a34a' }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#4caf50', letterSpacing: '0.08em', display: 'block', mb: 1.5 }}>
                            BUY / OVERWEIGHT
                          </Typography>
                          {(detail.summary.suggested_trades?.buy || []).length === 0 ? (
                            <Typography variant="caption" color="text.secondary">None flagged</Typography>
                          ) : (detail.summary.suggested_trades?.buy || []).map((item, i) => (
                            <Box key={i} sx={{ mb: 1.5, pb: 1.5, borderBottom: i < (detail.summary?.suggested_trades?.buy?.length ?? 0) - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Box sx={{
                                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                  bgcolor: item.conviction === 'high' ? '#4caf50' : item.conviction === 'medium' ? '#ff9800' : '#607d8b',
                                }} />
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.asset}</Typography>
                                {item.type && (
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase' }}>
                                    {item.type}
                                  </Typography>
                                )}
                                <Typography variant="caption" sx={{
                                  ml: 'auto', fontWeight: 600, fontSize: '0.6rem', px: 0.75, py: 0.25, borderRadius: 0.5,
                                  bgcolor: item.conviction === 'high' ? '#4caf5022' : item.conviction === 'medium' ? '#ff980022' : '#60707b22',
                                  color: item.conviction === 'high' ? '#4caf50' : item.conviction === 'medium' ? '#ff9800' : '#90a4ae',
                                }}>
                                  {item.conviction?.toUpperCase()}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block', pl: 2 }}>
                                {item.rationale}
                              </Typography>
                            </Box>
                          ))}
                        </Paper>

                        {/* AVOID column */}
                        <Paper elevation={0} sx={{ p: 2, bgcolor: bloomberg.bg.secondary, borderRadius: 1.5, borderTop: '2px solid #dc2626' }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#ef5350', letterSpacing: '0.08em', display: 'block', mb: 1.5 }}>
                            AVOID / UNDERWEIGHT
                          </Typography>
                          {(detail.summary.suggested_trades?.avoid || []).length === 0 ? (
                            <Typography variant="caption" color="text.secondary">None flagged</Typography>
                          ) : (detail.summary.suggested_trades?.avoid || []).map((item, i) => (
                            <Box key={i} sx={{ mb: 1.5, pb: 1.5, borderBottom: i < (detail.summary?.suggested_trades?.avoid?.length ?? 0) - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Box sx={{
                                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                  bgcolor: item.conviction === 'high' ? '#dc2626' : item.conviction === 'medium' ? '#ff9800' : '#607d8b',
                                }} />
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.asset}</Typography>
                                {item.type && (
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase' }}>
                                    {item.type}
                                  </Typography>
                                )}
                                <Typography variant="caption" sx={{
                                  ml: 'auto', fontWeight: 600, fontSize: '0.6rem', px: 0.75, py: 0.25, borderRadius: 0.5,
                                  bgcolor: item.conviction === 'high' ? '#dc262622' : item.conviction === 'medium' ? '#ff980022' : '#60707b22',
                                  color: item.conviction === 'high' ? '#ef5350' : item.conviction === 'medium' ? '#ff9800' : '#90a4ae',
                                }}>
                                  {item.conviction?.toUpperCase()}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block', pl: 2 }}>
                                {item.rationale}
                              </Typography>
                            </Box>
                          ))}
                        </Paper>
                      </Box>
                    </Box>
                  )}

                  {/* Asset Allocation Dashboard */}
                  {detail.summary.asset_allocation && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Asset Allocation
                      </Typography>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: bloomberg.bg.secondary, borderRadius: 1.5 }}>
                        <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, height: 28, borderRadius: 1, overflow: 'hidden' }}>
                          {[
                            { key: 'equities_pct', label: 'Equities', color: '#4caf50' },
                            { key: 'fixed_income_pct', label: 'Fixed Income', color: '#2196f3' },
                            { key: 'commodities_pct', label: 'Commodities', color: '#ff9800' },
                            { key: 'alternatives_pct', label: 'Alternatives', color: '#9c27b0' },
                            { key: 'cash_pct', label: 'Cash', color: '#607d8b' },
                          ].filter(({ key }) => (detail.summary?.asset_allocation as any)[key] > 0)
                           .map(({ key, color }) => (
                            <Box
                              key={key}
                              sx={{
                                width: `${(detail.summary?.asset_allocation as any)[key]}%`,
                                bgcolor: color,
                                borderRadius: 0.5,
                                minWidth: 4,
                              }}
                            />
                          ))}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          {[
                            { key: 'equities_pct', label: 'Equities', color: '#4caf50' },
                            { key: 'fixed_income_pct', label: 'Fixed Income', color: '#2196f3' },
                            { key: 'commodities_pct', label: 'Commodities', color: '#ff9800' },
                            { key: 'alternatives_pct', label: 'Alternatives', color: '#9c27b0' },
                            { key: 'cash_pct', label: 'Cash', color: '#607d8b' },
                          ].filter(({ key }) => (detail.summary?.asset_allocation as any)[key] > 0)
                           .map(({ key, label, color }) => (
                            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {label}: {(detail.summary?.asset_allocation as any)[key]}%
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                        {detail.summary.asset_allocation.rationale && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                            {detail.summary.asset_allocation.rationale}
                          </Typography>
                        )}
                      </Paper>
                    </Box>
                  )}

                  {/* Investment Implications */}
                  {detail.summary.investment_implications && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Investment Implications
                      </Typography>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        {/* Bullish */}
                        {detail.summary.investment_implications.bullish_signals?.length > 0 && (
                          <Paper elevation={0} sx={{ p: 2, bgcolor: bloomberg.status.positiveBg, borderRadius: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                              <TrendingUpIcon sx={{ color: bloomberg.status.positive, fontSize: 18 }} />
                              <Typography variant="subtitle2" sx={{ color: bloomberg.status.positive, fontWeight: 700 }}>
                                Bullish Signals
                              </Typography>
                            </Box>
                            {detail.summary.investment_implications.bullish_signals.map((s, i) => (
                              <Typography key={i} variant="body2" sx={{ mb: 0.5, pl: 0.5 }}>
                                • {s}
                              </Typography>
                            ))}
                          </Paper>
                        )}

                        {/* Bearish */}
                        {detail.summary.investment_implications.bearish_signals?.length > 0 && (
                          <Paper elevation={0} sx={{ p: 2, bgcolor: bloomberg.status.negativeBg, borderRadius: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                              <TrendingDownIcon sx={{ color: bloomberg.status.negative, fontSize: 18 }} />
                              <Typography variant="subtitle2" sx={{ color: bloomberg.status.negative, fontWeight: 700 }}>
                                Bearish Signals
                              </Typography>
                            </Box>
                            {detail.summary.investment_implications.bearish_signals.map((s, i) => (
                              <Typography key={i} variant="body2" sx={{ mb: 0.5, pl: 0.5 }}>
                                • {s}
                              </Typography>
                            ))}
                          </Paper>
                        )}
                      </Box>

                      {/* Tickers Mentioned */}
                      {detail.summary.investment_implications.tickers_mentioned?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                            Tickers Mentioned
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {detail.summary.investment_implications.tickers_mentioned.map((t, i) => (
                              <TickerChip key={i} symbol={t} />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Sectors */}
                      {detail.summary.investment_implications.sectors_mentioned?.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                            Sectors
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {detail.summary.investment_implications.sectors_mentioned.map((s, i) => (
                              <Chip key={i} label={s} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Macro Themes */}
                      {detail.summary.investment_implications.macro_themes?.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                            Macro Themes
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {detail.summary.investment_implications.macro_themes.map((m, i) => (
                              <Chip key={i} label={m} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Actionable Ideas */}
                      {detail.summary.investment_implications.actionable_ideas?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                            Actionable Ideas
                          </Typography>
                          {detail.summary.investment_implications.actionable_ideas.map((idea, i) => (
                            <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                              {i + 1}. {idea}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Full Transcript Toggle */}
                  {detail.transcript && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        size="small"
                        startIcon={<ArticleIcon />}
                        onClick={() => setShowTranscript(!showTranscript)}
                        sx={{ textTransform: 'none', mb: 1 }}
                      >
                        {showTranscript ? 'Hide Full Transcript' : 'Show Full Transcript'}
                      </Button>
                      <Collapse in={showTranscript}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            maxHeight: 400,
                            overflow: 'auto',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, fontSize: '0.8rem' }}
                          >
                            {detail.transcript}
                          </Typography>
                        </Paper>
                      </Collapse>
                    </Box>
                  )}
                </Box>
              )}

              {detail && detail.id === p.id && !detail.summary && !detailLoading && (
                <Alert severity="warning">Summary not available for this podcast.</Alert>
              )}
            </Box>
          </Collapse>
        </Card>
      ))}

      {/* No filter results */}
      {allCompleted.length > 0 && completedPodcasts.length === 0 && hasFilters && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No podcasts match the current filters.
          </Typography>
        </Paper>
      )}

      {/* Empty State */}
      {podcasts.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <PodcastsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
            No podcasts yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Paste a podcast URL above to get started. The AI will transcribe and summarize it from an investment analyst perspective.
          </Typography>
        </Paper>
      )}

      </> /* end Episodes tab */}
    </Box>
  );
};

export default Podcasts;
